import { z } from 'zod';

/**
 * RBAC matrix — BACKEND-SPEC §11 plus the role clarifications of ТЗ §7 («Доозначення ролей»).
 *
 * The API is the enforcement point. The SPA reads the same matrix only to hide what the
 * API would refuse anyway; hiding is never the control.
 */

/**
 * The seven runtime roles of `users.role` (BACKEND-SPEC §1.11), in spec order.
 *
 * They are the DB enum `user_role` one-to-one (`packages/db/src/schema/enums.ts`): a role
 * the matrix does not know cannot be stored, and a stored label the matrix does not know
 * (a future enum value reaching an older process) resolves to `null` in `toRole()` and is
 * refused everywhere — never degraded to a "harmless" role, because no role is harmless
 * once `viewer` can read the dashboards.
 */
export const ROLES = [
  'super_admin',
  'seo_lead',
  'seo',
  'content',
  'linkbuilder',
  'devops',
  'viewer',
] as const;
export type Role = (typeof ROLES)[number];

/**
 * Every role can be handed to a person — including `viewer`, which the spec lists as a real
 * job («звіти й дашборди»). Who may hand them out is a separate question answered by
 * `canAssignRole`: only `super_admin` manages users (ТЗ §7: «юзерів створює super_admin»;
 * BACKEND-SPEC §11: «Юзери, ролі, API-ключі» ✓ only for super_admin).
 */
export const ASSIGNABLE_ROLES = ROLES;
export type AssignableRole = Role;

/** Input schema for any procedure that assigns a role. */
export const assignableRoleSchema = z.enum(ASSIGNABLE_ROLES);
export const roleSchema = z.enum(ROLES);

/**
 * May `actor` assign `target` to someone? Only super_admin manages users, and it may assign
 * any of the seven — including another super_admin.
 */
export function canAssignRole(actor: Role, target: Role): boolean {
  return actor === 'super_admin' && (ROLES as readonly string[]).includes(target);
}

/**
 * Twelve zones of BACKEND-SPEC §11 plus `monitoring`: §11 has no row for it, but ТЗ §7 names
 * «Моніторинг та абузи» as the zone `devops` gets in full — a zone that has to exist for a
 * role to be expressible in it (Decision #20).
 */
export const ZONES = [
  'dashboards',
  'databases',
  'geo_dictionaries',
  'site_seo',
  'ai_generation',
  'page_editor',
  'emd',
  'bulk_ops',
  'tech_layer',
  'hosting',
  'monitoring',
  'linkbuilding',
  'system_admin',
] as const;
export type Zone = (typeof ZONES)[number];

export const ACTIONS = ['read', 'write', 'delete', 'execute'] as const;
export type Action = (typeof ACTIONS)[number];

/** Spec cell values: ✓ → full, «частково» → partial, «читання» → read, ✗ → none. */
export type Level = 'none' | 'read' | 'partial' | 'full';
export const LEVELS = ['none', 'read', 'partial', 'full'] as const;

/**
 * What a level lets a role do. The package never defines «частково»; the reading here is
 * the narrowest that still makes the cell useful — read and write, but never the actions
 * that destroy (`delete`) or spend/run (`execute`, e.g. EMD purchase).
 */
export const LEVEL_ACTIONS: Record<Level, readonly Action[]> = {
  none: [],
  read: ['read'],
  partial: ['read', 'write'],
  full: ['read', 'write', 'delete', 'execute'],
};

export const ZONE_LABELS: Record<Zone, string> = {
  dashboards: 'Дашборди та звіти',
  databases: 'Бази: бренди / ігри / кластери',
  geo_dictionaries: 'ГЕО-пресети та словники',
  site_seo: 'SEO-параметри сайтів',
  ai_generation: 'AI-генерація та промпти',
  page_editor: 'Редактор сторінок',
  emd: 'EMD генератор та купівля',
  bulk_ops: 'Масові переклейки, каноніки',
  tech_layer: 'Дропи, редиректи, hreflang',
  hosting: 'Хостинг, DNS, деплой',
  monitoring: 'Моніторинг та абузи',
  linkbuilding: 'Лінкбілдинг та анкори',
  system_admin: 'Юзери, ролі, API-ключі',
};

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super admin',
  seo_lead: 'SEO lead',
  seo: 'SEO спеціаліст',
  content: 'Контент',
  linkbuilder: 'Лінкбілдер',
  devops: 'DevOps',
  viewer: 'Viewer',
};

/**
 * The matrix itself — BACKEND-SPEC §11 (lines 461-474) cell for cell, in the same zone order,
 * with the three columns §11 does not draw filled in from ТЗ §7 «Доозначення ролей»:
 *
 * - `seo_lead` = the `seo` column («різниця організаційна — ширший geo_scope, зони ті самі»);
 * - `viewer` = «Дашборди та звіти» read only, everything else forbidden;
 * - `devops` = dashboards read + «Моніторинг та абузи» full + «Хостинг, DNS, деплей» full,
 *   everything else forbidden.
 *
 * Bulk relinks / canonicals / hreflang / drops / redirects / users stay super_admin only in every
 * reading. The one cell the package leaves open is `monitoring` for the four §11 roles: §11 has
 * no such row, so `seo`/`seo_lead` keep the `partial` of Decision #20 (they are paged for
 * incidents in their GEOs and must be able to open one), `content` and `linkbuilder` get none.
 *
 * `emd` for `seo` is «частково» — a level that excludes `execute`, and `purchase` is the one
 * procedure that spends real money. The mockup caption «SEO LEAD = SEO + купівля EMD» loses to
 * ТЗ §7, which explicitly makes seo_lead's zones identical to seo's.
 */
export const PERMISSION_MATRIX: Record<Role, Record<Zone, Level>> = {
  super_admin: {
    dashboards: 'full',
    databases: 'full',
    geo_dictionaries: 'full',
    site_seo: 'full',
    ai_generation: 'full',
    page_editor: 'full',
    emd: 'full',
    bulk_ops: 'full',
    tech_layer: 'full',
    hosting: 'full',
    monitoring: 'full',
    linkbuilding: 'full',
    system_admin: 'full',
  },
  seo_lead: {
    dashboards: 'full',
    databases: 'full',
    geo_dictionaries: 'full',
    site_seo: 'full',
    ai_generation: 'full',
    page_editor: 'full',
    emd: 'partial',
    bulk_ops: 'none',
    tech_layer: 'none',
    hosting: 'none',
    monitoring: 'partial',
    linkbuilding: 'full',
    system_admin: 'none',
  },
  seo: {
    dashboards: 'full',
    databases: 'full',
    geo_dictionaries: 'full',
    site_seo: 'full',
    ai_generation: 'full',
    page_editor: 'full',
    emd: 'partial',
    bulk_ops: 'none',
    tech_layer: 'none',
    hosting: 'none',
    monitoring: 'partial',
    linkbuilding: 'full',
    system_admin: 'none',
  },
  content: {
    dashboards: 'full',
    databases: 'full',
    geo_dictionaries: 'partial',
    site_seo: 'none',
    ai_generation: 'full',
    page_editor: 'full',
    emd: 'none',
    bulk_ops: 'none',
    tech_layer: 'none',
    hosting: 'none',
    monitoring: 'none',
    linkbuilding: 'partial',
    system_admin: 'none',
  },
  linkbuilder: {
    dashboards: 'full',
    databases: 'read',
    geo_dictionaries: 'none',
    site_seo: 'none',
    ai_generation: 'none',
    page_editor: 'none',
    emd: 'none',
    bulk_ops: 'none',
    tech_layer: 'none',
    hosting: 'none',
    monitoring: 'none',
    linkbuilding: 'full',
    system_admin: 'none',
  },
  devops: {
    dashboards: 'read',
    databases: 'none',
    geo_dictionaries: 'none',
    site_seo: 'none',
    ai_generation: 'none',
    page_editor: 'none',
    emd: 'none',
    bulk_ops: 'none',
    tech_layer: 'none',
    hosting: 'full',
    monitoring: 'full',
    linkbuilding: 'none',
    system_admin: 'none',
  },
  viewer: {
    dashboards: 'read',
    databases: 'none',
    geo_dictionaries: 'none',
    site_seo: 'none',
    ai_generation: 'none',
    page_editor: 'none',
    emd: 'none',
    bulk_ops: 'none',
    tech_layer: 'none',
    hosting: 'none',
    monitoring: 'none',
    linkbuilding: 'none',
    system_admin: 'none',
  },
};

/** The level a role has in a zone. */
export function levelFor(role: Role, zone: Zone): Level {
  return PERMISSION_MATRIX[role][zone];
}

export function can(role: Role, zone: Zone, action: Action): boolean {
  return LEVEL_ACTIONS[levelFor(role, zone)].includes(action);
}

export interface ZonePermission {
  label: string;
  level: Level;
  read: boolean;
  write: boolean;
  delete: boolean;
  execute: boolean;
}

/** Everything a role may do, zone by zone — the projection the SPA renders its shell from. */
export function zonePermissions(role: Role): Record<Zone, ZonePermission> {
  const out = {} as Record<Zone, ZonePermission>;
  for (const zone of ZONES) {
    const level = levelFor(role, zone);
    const actions = LEVEL_ACTIONS[level];
    out[zone] = {
      label: ZONE_LABELS[zone],
      level,
      read: actions.includes('read'),
      write: actions.includes('write'),
      delete: actions.includes('delete'),
      execute: actions.includes('execute'),
    };
  }
  return out;
}

/** A projection with no access at all — what an unrecognised role is shown. */
export function noZonePermissions(): Record<Zone, ZonePermission> {
  const out = {} as Record<Zone, ZonePermission>;
  for (const zone of ZONES) {
    out[zone] = {
      label: ZONE_LABELS[zone],
      level: 'none',
      read: false,
      write: false,
      delete: false,
      execute: false,
    };
  }
  return out;
}

/**
 * The DB-string boundary.
 *
 * `role` arrives from a Postgres enum column, so its type is an assertion, not a check: a
 * label added to `user_role` by a newer migration can reach a process built before it.
 * There is no safe role to degrade to — every row of the matrix grants something — so an
 * unknown label becomes `null`, and `requireActor` turns `null` into a clean 403 rather than
 * a TypeError or a quiet grant.
 */
export function toRole(value: string): Role | null {
  return (ROLES as readonly string[]).includes(value) ? (value as Role) : null;
}

/** Zones whose operations are irreversible network-wide; never delegated. */
export const SUPER_ADMIN_ONLY_ZONES: readonly Zone[] = [
  'bulk_ops',
  'tech_layer',
  'system_admin',
] as const;

/**
 * Roles exempt from `geo_scope` narrowing — they see the whole network.
 *
 * The spec says «для не-адмінів» without defining admin; the only role the enum has that
 * reads as one is super_admin, and ТЗ §7 gives seo_lead a *wider* geo_scope — meaningless
 * if it bypassed scoping. So super_admin alone is exempt.
 */
export const GEO_SCOPE_EXEMPT_ROLES: readonly Role[] = ['super_admin'] as const;

export function isGeoScoped(role: Role): boolean {
  return !GEO_SCOPE_EXEMPT_ROLES.includes(role);
}

/** TOTP is mandatory for super_admin (ТЗ §7) — enforced from the first login. */
export function requiresTwoFactor(role: Role): boolean {
  return role === 'super_admin';
}
